import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MessageSquare, Send, Plus, Pencil, Trash2, Zap, Clock, ChevronDown, ChevronUp, X, AlertTriangle } from 'lucide-react';
import {
  getNotificationTemplates, createNotificationTemplate, updateNotificationTemplate, deleteNotificationTemplate,
  getNotificationTriggers, updateNotificationTrigger,
  sendEmailBlast, sendSMSBlast, getNotificationHistory, getTeams
} from '../utils/api';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';

const TABS = [
  { id: 'compose', label: 'Compose', icon: Send },
  { id: 'templates', label: 'Templates', icon: Mail },
  { id: 'triggers', label: 'Automations', icon: Zap },
  { id: 'history', label: 'History', icon: Clock },
];

const AdminNotifications = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('compose');
  const [templates, setTemplates] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [history, setHistory] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Compose state
  const [composeChannel, setComposeChannel] = useState('email');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeFilter, setComposeFilter] = useState('all');
  const [composeSMS, setComposeSMS] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [searchTemplates, setSearchTemplates] = useState('');

  // Template modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '', category: 'general' });

  const token = localStorage.getItem('agentToken');

  const loadData = useCallback(async () => {
    if (!token) { navigate('/agent/login'); return; }
    try {
      setIsLoading(true);
      const [tmplRes, trigRes, histRes, teamRes] = await Promise.all([
        getNotificationTemplates(token),
        getNotificationTriggers(token),
        getNotificationHistory(token),
        getTeams(token),
      ]);
      setTemplates(tmplRes.templates || []);
      setTriggers(trigRes.triggers || []);
      setHistory(histRes.logs || []);
      setTeams(teamRes.teams || []);
    } catch (e) {
      if (e.response?.status === 403) navigate('/agent/dashboard');
      else toast.error('Failed to load notifications data');
    } finally {
      setIsLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // ============================================
  // Compose Handlers
  // ============================================
  const handleSendEmail = async () => {
    if (!composeSubject.trim() || !composeBody.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setIsSending(true);
    try {
      const res = await sendEmailBlast({
        subject: composeSubject,
        body: composeBody,
        recipientFilter: composeFilter,
        channel: 'email',
      }, token);
      toast.success(`Email sent to ${res.sentCount}/${res.totalRecipients} recipients`);
      setComposeSubject('');
      setComposeBody('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSMS = async () => {
    if (!composeSMS.trim()) {
      toast.error('Message is required');
      return;
    }
    setIsSending(true);
    try {
      const res = await sendSMSBlast({
        message: composeSMS,
        recipientFilter: composeFilter,
      }, token);
      if (res.success) {
        toast.success(`SMS sent to ${res.totalRecipients} recipients`);
      } else {
        toast.warning(res.message || 'SMS service not configured');
      }
      setComposeSMS('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setIsSending(false);
    }
  };

  const applyTemplate = (tmpl) => {
    setComposeSubject(tmpl.subject);
    setComposeBody(tmpl.body);
    toast.success(`Template "${tmpl.name}" applied`);
  };

  // ============================================
  // Template Handlers
  // ============================================
  const openTemplateModal = (tmpl = null) => {
    if (tmpl) {
      setEditingTemplate(tmpl);
      setTemplateForm({ name: tmpl.name, subject: tmpl.subject, body: tmpl.body, category: tmpl.category || 'general' });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '', category: 'general' });
    }
    setShowTemplateModal(true);
    setShowTemplatePreview(false);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim()) {
      toast.error('Name and subject are required');
      return;
    }
    try {
      if (editingTemplate) {
        await updateNotificationTemplate(editingTemplate.id, templateForm, token);
        toast.success('Template updated');
      } else {
        await createNotificationTemplate(templateForm, token);
        toast.success('Template created');
      }
      setShowTemplateModal(false);
      loadData();
    } catch (e) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await deleteNotificationTemplate(id, token);
      toast.success('Template deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (tmpl) => {
    try {
      await createNotificationTemplate({
        name: `${tmpl.name} (Copy)`,
        subject: tmpl.subject,
        body: tmpl.body,
        category: tmpl.category || 'general',
      }, token);
      toast.success('Template duplicated');
      loadData();
    } catch (e) {
      toast.error('Failed to duplicate template');
    }
  };

  const filteredTemplates = searchTemplates
    ? templates.filter(t =>
        t.name.toLowerCase().includes(searchTemplates.toLowerCase()) ||
        t.subject.toLowerCase().includes(searchTemplates.toLowerCase()) ||
        (t.category || '').toLowerCase().includes(searchTemplates.toLowerCase())
      )
    : templates;

  // ============================================
  // Trigger Handlers
  // ============================================
  const handleToggleTrigger = async (trigger) => {
    try {
      await updateNotificationTrigger(trigger.triggerId, {
        triggerId: trigger.triggerId,
        enabled: !trigger.enabled,
        templateId: trigger.templateId,
      }, token);
      toast.success(`Trigger ${!trigger.enabled ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (e) {
      toast.error('Failed to update trigger');
    }
  };

  const handleTriggerTemplateChange = async (trigger, templateId) => {
    try {
      await updateNotificationTrigger(trigger.triggerId, {
        triggerId: trigger.triggerId,
        enabled: trigger.enabled,
        templateId: templateId || null,
      }, token);
      toast.success('Trigger template updated');
      loadData();
    } catch (e) {
      toast.error('Failed to update trigger');
    }
  };

  if (isLoading) {
    return (
      <AgentLayout title="Notifications">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Notifications">
      <div data-testid="notifications-page">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md p-2 mb-6 overflow-x-auto" data-testid="notification-tabs">
          <div className="flex gap-2 min-w-max">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-md font-semibold transition text-sm whitespace-nowrap ${
                    activeTab === tab.id ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`notif-tab-${tab.id}`}
                >
                  <Icon size={16} /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <div className="space-y-4" data-testid="compose-section">
            {/* Channel Toggle */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => setComposeChannel('email')}
                  className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold transition ${
                    composeChannel === 'email' ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="channel-email-btn"
                >
                  <Mail size={16} /> Email Blast
                </button>
                <button
                  onClick={() => setComposeChannel('sms')}
                  className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold transition ${
                    composeChannel === 'sms' ? 'bg-brand-orange text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="channel-sms-btn"
                >
                  <MessageSquare size={16} /> SMS Blast
                </button>
              </div>

              {/* Recipient Filter */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Recipients</label>
                <select
                  value={composeFilter}
                  onChange={e => setComposeFilter(e.target.value)}
                  className="w-full sm:w-64 px-3 py-2 min-h-[44px] border rounded-lg text-sm bg-white"
                  data-testid="recipient-filter"
                >
                  <option value="all">All Agents</option>
                  <option value="active">Active Agents Only</option>
                  {teams.map(t => (
                    <option key={t.name} value={`team:${t.name}`}>Team: {t.name}</option>
                  ))}
                </select>
              </div>

              {composeChannel === 'email' ? (
                <>
                  {/* Quick template apply */}
                  {templates.length > 0 && (
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Quick Apply Template</label>
                      <div className="flex flex-wrap gap-2">
                        {templates.map(t => (
                          <button
                            key={t.id}
                            onClick={() => applyTemplate(t)}
                            className="px-3 py-1.5 text-xs bg-blue-50 text-brand-blue border border-blue-200 rounded-lg hover:bg-blue-100 transition"
                            data-testid={`apply-template-${t.id}`}
                          >
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Subject</label>
                    <input
                      type="text"
                      value={composeSubject}
                      onChange={e => setComposeSubject(e.target.value)}
                      className="w-full px-3 py-2 min-h-[44px] border rounded-lg text-sm"
                      placeholder="Email subject line..."
                      data-testid="compose-subject"
                    />
                  </div>
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-500 uppercase">
                        Body <span className="text-gray-400 normal-case">(HTML supported)</span>
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowPreview(!showPreview)}
                          className={`px-3 py-1 text-xs rounded-md font-medium transition ${showPreview ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid="toggle-preview-btn"
                        >
                          {showPreview ? 'Edit' : 'Preview'}
                        </button>
                      </div>
                    </div>
                    {/* Variable insertion buttons */}
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="variable-buttons">
                      {[
                        { label: 'Agent Name', var: '{{agent_name}}' },
                        { label: 'Date', var: '{{date}}' },
                        { label: 'Company', var: '{{company_name}}' },
                        { label: 'Agent Email', var: '{{agent_email}}' },
                        { label: 'Team', var: '{{team}}' },
                      ].map(v => (
                        <button
                          key={v.var}
                          onClick={() => { if (!showPreview) setComposeBody(prev => prev + v.var); }}
                          className="px-2 py-1 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded font-mono hover:bg-purple-100 transition"
                          title={`Insert ${v.var}`}
                          data-testid={`insert-var-${v.label.toLowerCase().replace(' ', '-')}`}
                        >
                          {v.var}
                        </button>
                      ))}
                    </div>
                    {showPreview ? (
                      <div
                        className="w-full px-4 py-3 border rounded-lg text-sm min-h-[200px] bg-white prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: composeBody.replace(/\{\{agent_name\}\}/g, 'John Smith').replace(/\{\{date\}\}/g, new Date().toLocaleDateString()).replace(/\{\{company_name\}\}/g, 'Purposefully Covered Insurance Group LLC') }}
                        data-testid="compose-preview"
                      />
                    ) : (
                      <textarea
                        value={composeBody}
                        onChange={e => setComposeBody(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm min-h-[200px] font-mono"
                        placeholder="<h2>Hello {{agent_name}},</h2>\n<p>Your email content here...</p>"
                        data-testid="compose-body"
                      />
                    )}
                  </div>
                  <button
                    onClick={handleSendEmail}
                    disabled={isSending}
                    className="flex items-center gap-2 px-6 py-2 min-h-[44px] bg-brand-blue text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition disabled:opacity-50"
                    data-testid="send-email-btn"
                  >
                    <Send size={16} /> {isSending ? 'Sending...' : 'Send Email Blast'}
                  </button>
                </>
              ) : (
                <>
                  <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2" data-testid="sms-warning">
                    <AlertTriangle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800">
                      SMS sending requires Twilio credentials. The system is scaffolded and ready - provide your TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_PHONE_NUMBER to activate.
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                      Message <span className="text-gray-400 normal-case">(160 char limit)</span>
                    </label>
                    <textarea
                      value={composeSMS}
                      onChange={e => setComposeSMS(e.target.value.slice(0, 160))}
                      className="w-full px-3 py-2 border rounded-lg text-sm min-h-[100px]"
                      placeholder="Your SMS message here..."
                      maxLength={160}
                      data-testid="compose-sms"
                    />
                    <p className="text-xs text-gray-400 mt-1">{composeSMS.length}/160 characters</p>
                  </div>
                  <button
                    onClick={handleSendSMS}
                    disabled={isSending}
                    className="flex items-center gap-2 px-6 py-2 min-h-[44px] bg-brand-orange text-white rounded-lg font-semibold text-sm hover:bg-orange-600 transition disabled:opacity-50"
                    data-testid="send-sms-btn"
                  >
                    <MessageSquare size={16} /> {isSending ? 'Sending...' : 'Send SMS Blast'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div data-testid="templates-section">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h3 className="text-lg font-bold text-gray-900">Email Templates</h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTemplates}
                  onChange={e => setSearchTemplates(e.target.value)}
                  placeholder="Search templates..."
                  className="px-3 py-2 min-h-[44px] border rounded-lg text-sm w-48"
                  data-testid="search-templates-input"
                />
                <button
                  onClick={() => openTemplateModal()}
                  className="flex items-center gap-2 px-4 py-2 min-h-[44px] bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition whitespace-nowrap"
                  data-testid="create-template-btn"
                >
                  <Plus size={16} /> New Template
                </button>
              </div>
            </div>

            <div className="grid gap-4">
              {filteredTemplates.map(tmpl => {
                const catColor = tmpl.category === 'welcome' ? 'bg-green-100 text-green-700 border-green-200' :
                  tmpl.category === 'reminder' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                  tmpl.category === 'announcement' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                  'bg-gray-100 text-gray-600 border-gray-200';
                return (
                <div key={tmpl.id} className="bg-white rounded-lg shadow-md p-4" data-testid={`template-card-${tmpl.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h4 className="font-bold text-gray-900">{tmpl.name}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${catColor}`}>{tmpl.category}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDuplicateTemplate(tmpl)}
                        className="flex items-center gap-1 px-3 py-1.5 min-h-[36px] text-xs bg-blue-50 text-brand-blue rounded-md hover:bg-blue-100 transition"
                        data-testid={`duplicate-template-${tmpl.id}`}
                        title="Duplicate"
                      >
                        <Plus size={12} /> Duplicate
                      </button>
                      <button
                        onClick={() => openTemplateModal(tmpl)}
                        className="flex items-center gap-1 px-3 py-1.5 min-h-[36px] text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition"
                        data-testid={`edit-template-${tmpl.id}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(tmpl.id)}
                        className="flex items-center gap-1 px-3 py-1.5 min-h-[36px] text-xs bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition"
                        data-testid={`delete-template-${tmpl.id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mb-1"><strong>Subject:</strong> {tmpl.subject}</p>
                  <div className="text-xs text-gray-400 bg-gray-50 p-2 rounded max-h-24 overflow-hidden" dangerouslySetInnerHTML={{ __html: tmpl.body }} />
                </div>
              );})}
              {filteredTemplates.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Mail size={40} className="mx-auto mb-3" />
                  <p>{searchTemplates ? 'No templates match your search.' : 'No templates yet. Create your first one!'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Triggers Tab */}
        {activeTab === 'triggers' && (
          <div data-testid="triggers-section">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Automated Triggers</h3>
            <p className="text-sm text-gray-500 mb-6">Configure automatic notifications that are sent when specific events occur.</p>
            <div className="space-y-3">
              {triggers.map(trigger => (
                <div key={trigger.triggerId} className="bg-white rounded-lg shadow-md p-4" data-testid={`trigger-${trigger.triggerId}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h4 className="font-bold text-gray-900">{trigger.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          trigger.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {trigger.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{trigger.description}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <select
                        value={trigger.templateId || ''}
                        onChange={e => handleTriggerTemplateChange(trigger, e.target.value)}
                        className="px-3 py-2 min-h-[44px] border rounded-lg text-sm bg-white w-full sm:w-48"
                        data-testid={`trigger-template-select-${trigger.triggerId}`}
                      >
                        <option value="">No template</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleToggleTrigger(trigger)}
                        className={`px-4 py-2 min-h-[44px] rounded-lg text-sm font-semibold transition ${
                          trigger.enabled
                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                        data-testid={`trigger-toggle-${trigger.triggerId}`}
                      >
                        {trigger.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div data-testid="history-section">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Notification History</h3>
            {history.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Clock size={40} className="mx-auto mb-3" />
                <p>No notifications sent yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(log => (
                  <div key={log.id} className="bg-white rounded-lg shadow-md p-4" data-testid={`history-item-${log.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        {log.type === 'email_blast' ? (
                          <Mail size={18} className="text-brand-blue" />
                        ) : (
                          <MessageSquare size={18} className="text-brand-orange" />
                        )}
                        <span className="font-bold text-gray-900 text-sm">
                          {log.type === 'email_blast' ? log.subject : 'SMS Blast'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-brand-blue">
                          {log.type === 'email_blast' ? 'Email' : 'SMS'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(log.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                      <span>Filter: <strong>{log.recipientFilter}</strong></span>
                      <span>Recipients: <strong>{log.totalRecipients}</strong></span>
                      <span>Sent: <strong className="text-green-600">{log.sentCount}</strong></span>
                      {log.errorCount > 0 && <span>Errors: <strong className="text-red-600">{log.errorCount}</strong></span>}
                      <span>By: <strong>{log.sentByEmail}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Template Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="template-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-bold text-lg">{editingTemplate ? 'Edit Template' : 'New Template'}</h3>
                <button onClick={() => setShowTemplateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100" data-testid="close-template-modal">
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Template Name</label>
                    <input
                      value={templateForm.name}
                      onChange={e => setTemplateForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-3 py-2 min-h-[44px] border rounded-lg text-sm"
                      placeholder="E.g., Welcome Email"
                      data-testid="template-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Category</label>
                    <select
                      value={templateForm.category}
                      onChange={e => setTemplateForm(p => ({ ...p, category: e.target.value }))}
                      className="w-full px-3 py-2 min-h-[44px] border rounded-lg text-sm bg-white"
                      data-testid="template-category-select"
                    >
                      <option value="general">General</option>
                      <option value="welcome">Welcome</option>
                      <option value="reminder">Reminder</option>
                      <option value="announcement">Announcement</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Subject</label>
                  <input
                    value={templateForm.subject}
                    onChange={e => setTemplateForm(p => ({ ...p, subject: e.target.value }))}
                    className="w-full px-3 py-2 min-h-[44px] border rounded-lg text-sm"
                    placeholder="Email subject line"
                    data-testid="template-subject-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase">Body</label>
                    <button
                      onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                      className={`px-3 py-1 text-xs rounded-md font-medium transition ${showTemplatePreview ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      data-testid="toggle-template-preview-btn"
                    >
                      {showTemplatePreview ? 'Edit' : 'Preview'}
                    </button>
                  </div>
                  {/* Variable insertion */}
                  {!showTemplatePreview && (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="template-variable-buttons">
                      {[
                        { label: 'Agent Name', var: '{{agent_name}}' },
                        { label: 'Date', var: '{{date}}' },
                        { label: 'Company', var: '{{company_name}}' },
                        { label: 'Agent Email', var: '{{agent_email}}' },
                        { label: 'Team', var: '{{team}}' },
                      ].map(v => (
                        <button
                          key={v.var}
                          onClick={() => setTemplateForm(p => ({ ...p, body: p.body + v.var }))}
                          className="px-2 py-1 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded font-mono hover:bg-purple-100 transition"
                        >
                          {v.var}
                        </button>
                      ))}
                    </div>
                  )}
                  {showTemplatePreview ? (
                    <div
                      className="w-full px-4 py-3 border rounded-lg text-sm min-h-[180px] bg-gray-50 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: templateForm.body.replace(/\{\{agent_name\}\}/g, 'John Smith').replace(/\{\{date\}\}/g, new Date().toLocaleDateString()).replace(/\{\{company_name\}\}/g, 'Purposefully Covered Insurance Group LLC') }}
                      data-testid="template-preview"
                    />
                  ) : (
                    <textarea
                      value={templateForm.body}
                      onChange={e => setTemplateForm(p => ({ ...p, body: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm min-h-[180px] font-mono"
                      placeholder="<h2>Hello {{agent_name}},</h2>"
                      data-testid="template-body-input"
                    />
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 p-4 border-t">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 min-h-[44px] bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition"
                  data-testid="cancel-template-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-4 py-2 min-h-[44px] bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                  data-testid="save-template-btn"
                >
                  {editingTemplate ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminNotifications;
